// Training test questions - testing real judgment, not memorization
// Designed to predict actual performance rating (1-5 scale)

export const TRAINING_TEST_QUESTIONS = [
    // DELIVERABLES - Speed - Time to Ship
    {
        id: "speed_1",
        criterion: "timeToShip",
        pillar: "deliverables",
        type: "scenario",
        question: "Feature is 90% done but has an edge case affecting 2% of users. Client demo is tomorrow. What do you do?",
        options: [
            "Delay demo until 100% done - quality matters",
            "Ship it now, fix edge case later based on client feedback",
            "Ship with edge case documented, let client prioritize the fix",
            "Pull an all-nighter to get it to 100%"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 2, // B - delays unnecessarily
            1: 3, // A - ships but no transparency
            2: 5, // C - ships + transparent + prioritizes (exceptional judgment)
            3: 4  // D - ships but burns out
        },
        explanation: "Ship with transparency. Document the limitation, let the client see value immediately, and prioritize the fix based on real feedback. This shows speed + product thinking + ownership."
    },

    {
        id: "speed_2",
        criterion: "timeToShip",
        pillar: "deliverables",
        type: "scenario",
        question: "You're blocked waiting for API keys from client. It's been 2 days. The feature can't work without them. What do you do?",
        options: [
            "Keep reminding them daily until they send keys",
            "Mock the API responses, build the feature, swap in real API when keys arrive",
            "Work on other tasks, circle back when keys arrive",
            "Escalate to project manager to get keys faster"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 2, // passive waiting
            1: 5, // unblocks self creatively (débrouillardise)
            2: 1, // completely blocked
            3: 3  // escalates but doesn't unblock self
        },
        explanation: "Débrouillardise - never stay blocked. Mock the API, build the feature, validate the integration pattern. Swap in real API when ready. This is how you ship fast without perfect conditions."
    },

    // DELIVERABLES - Quality - Product Thinking
    {
        id: "quality_1",
        criterion: "productThinking",
        pillar: "deliverables",
        type: "scenario",
        question: "Client asks for user roles (Admin, Editor, Viewer). You realize they have 3 users, all admins. What do you do?",
        options: [
            "Build it - they asked for it, they know their business",
            "Build simplified version: admin vs non-admin only",
            "Challenge it: 'Why do you need roles with 3 admin users?' Dig into the real problem",
            "Add to backlog, suggest revisiting when they have more users"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 1, // just builds what's asked
            1: 3, // pragmatic simplification
            2: 5, // challenges, understands real need (exceptional product thinking)
            3: 4  // smart deferral
        },
        explanation: "Always dig into the 'why'. They might actually need audit logs, or are planning to hire, or misunderstand their own problem. Understanding the real need leads to better solutions."
    },

    {
        id: "quality_2",
        criterion: "codeQuality",
        pillar: "deliverables",
        type: "scenario",
        question: "You finish a booking feature. Form works, API works, data saves. But you realize users can't see their bookings. The ticket only mentioned 'creating bookings'. Do you ship?",
        options: [
            "Yes - ticket is done, viewing is a separate feature",
            "No - add a basic list view first, then ship",
            "Ship but document the limitation in the release notes",
            "Ask project manager if viewing is needed"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 1, // literal ticket interpretation
            1: 5, // thinks about what makes feature actually useful
            2: 2, // ships incomplete feature with excuse
            3: 2  // asks instead of thinking
        },
        explanation: "Think about what makes a feature actually usable. If users can't see their bookings, the feature is incomplete. Add minimal viewing functionality - it's part of making the feature work."
    },

    // DELIVERABLES - Complexity - Problem Difficulty
    {
        id: "complexity_1",
        criterion: "problemDifficulty",
        pillar: "deliverables",
        type: "scenario",
        question: "You need to sync 10,000 records between two systems daily. Direct API calls time out. What's your approach?",
        options: [
            "Call API in a loop with delays between calls",
            "Batch records (500 at a time), process sequentially with error handling",
            "Design queue system: chunk records, process async, handle failures, track progress",
            "Ask client to reduce number of records"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 2, // basic but inefficient
            1: 3, // solid solution
            2: 5, // systems thinking (advanced architecture)
            3: 1  // avoids the problem
        },
        explanation: "Scale problems need system design. Queue-based processing handles failures gracefully, doesn't block, can restart, and tracks progress. This is advanced problem-solving."
    },

    // SKILLS - Code Readability
    {
        id: "readability_1",
        criterion: "codeReadability",
        pillar: "skills",
        type: "code_review",
        question: "Which approach is clearest for validating: user logged in, has permission, resource exists?",
        options: [
            "if (user && hasPermission(user) && resource) { doThing(); }",
            "if (!user) return error('Not logged in'); if (!hasPermission(user)) return error('No permission'); if (!resource) return error('Not found'); doThing();",
            "const valid = user && hasPermission(user) && resource; if (!valid) return error('Invalid'); doThing();",
            "try { assert(user); assert(hasPermission(user)); assert(resource); doThing(); } catch { return error(); }"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 2, // works but nested
            1: 5, // early returns, specific errors (clearest)
            2: 1, // loses error context
            3: 3  // structured but not idiomatic
        },
        explanation: "Early returns with specific error messages. Each check is clear, errors are specific, and the 'happy path' isn't nested. This is readable code."
    },

    // SKILLS - API Patterns
    {
        id: "api_1",
        criterion: "apiPatterns",
        pillar: "skills",
        type: "scenario",
        question: "Endpoint needs to search users by: name (partial), email (exact), roles (multiple), created date range. How do you design it?",
        options: [
            "GET /users?name=John&email=test@test.com&roles=admin,user&from=2024-01-01&to=2024-12-31",
            "POST /users/search with body { name, email, roles: [], dateRange: { from, to } }",
            "GET /users/search with query params",
            "Multiple endpoints: /users/by-name, /users/by-email, /users/by-role"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 2, // works but messy with arrays
            1: 5, // correct pattern for complex filters
            2: 2, // same as A but worse
            3: 1  // over-complicates
        },
        explanation: "POST /search for complex filters. Handles arrays, nested objects, and date ranges cleanly. GET query params get messy with complex structures."
    },

    {
        id: "api_2",
        criterion: "apiPatterns",
        pillar: "skills",
        type: "code_review",
        question: "API returns `{ users: [...], total: 42 }`. What's missing for a good search endpoint?",
        options: [
            "Nothing - this is fine",
            "Should use { ok: true, data: [...users...], total: 42 } format",
            "Should include pagination: { users, total, page, perPage }",
            "Both B and C"
        ],
        correctAnswer: 3,
        scoreMapping: {
            0: 1, // doesn't see the issue
            1: 3, // sees format issue but misses pagination
            2: 3, // sees pagination but misses format
            3: 5  // sees both - correct format (data = array) + pagination
        },
        explanation: "Consistent {ok, data} format where data holds the array directly, plus pagination info (page, perPage). This makes error handling uniform and pagination straightforward across all endpoints."
    },

    {
        id: "api_3",
        criterion: "apiPatterns",
        pillar: "skills",
        type: "scenario",
        question: "You're building POST /bookings. It should create a booking, send confirmation email, and update a calendar. How do you structure it?",
        options: [
            "All in one route: create booking, send email, update calendar sequentially",
            "Create booking only, let frontend trigger email and calendar separately",
            "Create booking, then trigger email and calendar as background jobs",
            "Split into POST /bookings, POST /bookings/:id/notify, POST /bookings/:id/sync-calendar"
        ],
        correctAnswer: 0,
        scoreMapping: {
            0: 5, // correct - one route creates one object, side effects are fine
            1: 1, // wrong - frontend shouldn't orchestrate backend side effects
            2: 3, // reasonable for heavy operations but over-engineers simple case
            3: 1  // violates one route one object - these aren't separate resources
        },
        explanation: "One POST route = one object created. Side effects (emails, calendar sync) happen inside that route. Don't split side effects into separate endpoints - booking + its side effects are one operation."
    },

    // SKILLS - Frontend Patterns
    {
        id: "frontend_1",
        criterion: "frontendPatterns",
        pillar: "skills",
        type: "code_review",
        question: "After creating a venue: `const result = await api.post('/venues', data); if (result.ok) setVenues([...venues, result.data]);` What's wrong?",
        options: [
            "Nothing - this works fine",
            "Missing error handling for when ok is false",
            "Should call fetchVenues() instead of manually updating state",
            "Both B and C"
        ],
        correctAnswer: 3,
        scoreMapping: {
            0: 1, // doesn't see issues
            1: 2, // sees one issue
            2: 3, // sees the better pattern
            3: 5  // sees both (complete understanding)
        },
        explanation: "Always handle errors AND refresh from server. Manually adding to state can cause inconsistencies. Server is source of truth - fetch the latest data."
    },

    {
        id: "frontend_2",
        criterion: "frontendPatterns",
        pillar: "skills",
        type: "scenario",
        question: "You need user data in 3 sibling components on the same page. How do you handle it?",
        options: [
            "Each component calls api.get('/user') independently",
            "Parent fetches once, passes user as prop to all 3 components",
            "Create a UserContext provider wrapping all 3 components",
            "Store user in Redux/global state"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 1, // wasteful - 3 identical API calls
            1: 5, // correct - fetch once in parent, pass down
            2: 2, // over-engineers with context for simple data passing
            3: 1  // over-engineers with global state
        },
        explanation: "For sibling components needing the same data, fetch in the parent and pass down as props. Don't make 3 identical API calls, and don't over-engineer with Context or Redux for simple prop passing."
    },

    {
        id: "frontend_3",
        criterion: "frontendPatterns",
        pillar: "skills",
        type: "scenario",
        question: "You have a list of 1000 users. Client wants to filter by name, role, and status. Where do you implement the filtering?",
        options: [
            "Frontend - filter the array in JavaScript after fetching",
            "Backend - add filters to POST /users/search endpoint",
            "Both - fetch all, then filter in frontend for better UX",
            "Create a separate filtering service layer"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 1, // wrong - filters should be in backend
            1: 5, // correct - filtering is backend responsibility
            2: 1, // wrong - don't filter in frontend
            3: 2  // over-engineers with service layer
        },
        explanation: "Filters belong in the backend. Frontend filtering means fetching all data, wasting bandwidth and memory. Backend filtering is efficient, scalable, and the correct pattern."
    },

    {
        id: "frontend_5",
        criterion: "frontendPatterns",
        pillar: "skills",
        type: "code_review",
        question: "Component stores: `const [userName, setUserName] = useState(''); const [userEmail, setUserEmail] = useState('');` What's better?",
        options: [
            "Keep as-is - separate state for each field",
            "Store full user object: `const [user, setUser] = useState(null)`",
            "Use a reducer for complex state management",
            "Create a custom hook for user state"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 1, // wrong - partial data extraction
            1: 5, // correct - store full object, not pieces
            2: 2, // over-engineers
            3: 2  // over-engineers with abstraction
        },
        explanation: "Store the full object, not pieces of it. `const [user, setUser] = useState(null)` keeps data together, avoids sync issues, and follows the 'no partial data extraction' principle."
    },

    // SKILLS - Complexity Management
    {
        id: "complexity_1",
        criterion: "complexityManagement",
        pillar: "skills",
        type: "scenario",
        question: "Function calls 3 APIs sequentially (each needs previous result). How do you handle errors?",
        options: [
            "try/catch around all three - return generic error if any fails",
            "try/catch each separately - return which step failed",
            "try/catch each - return user-friendly message, log technical details for debugging",
            "No try/catch - let errors bubble to global handler"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 2, // catches but not useful
            1: 3, // better - specific errors
            2: 5, // excellent - user-friendly + debuggable
            3: 1  // no error handling
        },
        explanation: "Separate try/catch per step. User gets clear feedback ('Failed to fetch user data'), you get logs showing exactly where it failed ('Step 2: user.getPreferences threw 404')."
    },

    // SKILLS - Database Patterns
    {
        id: "database_1",
        criterion: "databasePatterns",
        pillar: "skills",
        type: "scenario",
        question: "You need to store a user with their address (street, city, zip). How do you structure it in MongoDB?",
        options: [
            "Nested: { name: 'John', address: { street: '123 Main', city: 'NYC', zip: '10001' } }",
            "Flat: { name: 'John', address_street: '123 Main', address_city: 'NYC', address_zip: '10001' }",
            "Separate collection for addresses, reference by ID",
            "JSON string: { name: 'John', address: '{\"street\":\"123 Main\",...}' }"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 1, // wrong - never nest in MongoDB
            1: 5, // correct - flat structure
            2: 2, // over-complicates for simple case
            3: 1  // wrong - string is not structured
        },
        explanation: "NEVER nest data in MongoDB. Use flat structure with prefixed keys. Nested data makes queries complex, indexing difficult, and violates our flat data principle."
    },

    // SKILLS - Problem Solving
    {
        id: "problem_1",
        criterion: "problemSolving",
        pillar: "skills",
        type: "scenario",
        question: "You've been stuck on a bug for 3 hours. Tried: checking logs, reviewing similar code, testing hypotheses. Nothing worked. Lead is in a meeting. What do you do?",
        options: [
            "Keep trying - you'll figure it out eventually",
            "Switch to easier task, come back to bug later",
            "Document what you tried, write concise message with context, ask for sync when lead is free",
            "Post in team chat: 'This bug is really hard, anyone can help?'"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 1, // stubborn inefficiency
            1: 1, // avoids problem
            2: 5, // structured communication, unblocks efficiently
            3: 2  // asks but not effectively
        },
        explanation: "After 3 hours, ask for help efficiently. Document attempts so others can help quickly. This is débrouillardise - being resourceful includes knowing when to get help."
    },

    // CULTURE - Ownership - End-to-End Responsibility
    {
        id: "ownership_1",
        criterion: "endToEndResponsibility",
        pillar: "culture",
        type: "scenario",
        question: "You deploy Friday 5pm. At 6pm a user reports data isn't saving. You have dinner plans. What do you do?",
        options: [
            "Tell them you'll look Monday",
            "Cancel dinner, fix immediately",
            "Quick check (10 min) - if critical cancel dinner, if minor fix Monday",
            "Pass to teammate who's online"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 1, // no ownership
            1: 4, // extreme ownership but poor judgment
            2: 5, // ownership + good judgment
            3: 1  // delegates own work
        },
        explanation: "Ownership means you fix what you deploy, but also means good judgment. Quick triage - if it's critical (data loss), fix now. If it's a minor UI bug, fix Monday."
    },

    {
        id: "ownership_2",
        criterion: "endToEndResponsibility",
        pillar: "culture",
        type: "scenario",
        question: "Something you deployed yesterday breaks in production. Users reporting errors. What do you do?",
        options: [
            "Check if it's really your code first - might be infrastructure",
            "Immediately investigate, identify issue, create hotfix, communicate status",
            "Add to your task list, fix it by end of day",
            "Check with team if anyone else can look at it"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 2, // blame-shifting
            1: 5, // immediate ownership + action
            2: 2, // delayed response
            3: 1  // avoids responsibility
        },
        explanation: "You deployed it, you own it. Immediate action - investigate, fix, communicate. Production issues are urgent. Blame-shifting wastes time."
    },

    // CULTURE - Ownership - No Boxes
    {
        id: "no_boxes_1",
        criterion: "noBoxes",
        pillar: "culture",
        type: "scenario",
        question: "API endpoint returns wrong data format, blocking your work. The code was written by someone else. What do you do?",
        options: [
            "Wait for the person who wrote it to fix it",
            "Work on something else until they're available",
            "Read the code, understand the issue, fix it yourself",
            "Create a workaround in your code to handle the wrong format"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 1, // blocked waiting
            1: 1, // passive avoidance
            2: 5, // no boxes - fixes root cause
            3: 3  // unblocks self but doesn't fix properly
        },
        explanation: "No boxes mentality - if you can fix it, fix it. Read the code, understand the issue, fix the root cause. Don't create workarounds for broken things. We all work across the stack."
    },

    {
        id: "no_boxes_2",
        criterion: "noBoxes",
        pillar: "culture",
        type: "scenario",
        question: "Reviewing PR, you notice a critical bug in unrelated code (not in the PR) that could cause data loss. What do you do?",
        options: [
            "Nothing - not in PR scope, not my problem",
            "Comment on the PR about the bug",
            "Fix it immediately in a separate commit or quick PR",
            "Tell the person who wrote that code about it"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 1, // strict boxes
            1: 2, // mentions but doesn't act
            2: 5, // no boxes - fixes immediately
            3: 2  // delegates instead of fixing
        },
        explanation: "If you see a critical bug and can fix it, fix it now. Team success > individual boundaries. Data loss bugs don't wait for 'proper' assignment."
    },

    // CULTURE - Ownership - Commercial Mindset
    {
        id: "commercial_1",
        criterion: "commercialMindset",
        pillar: "culture",
        type: "scenario",
        question: "Client wants 'admin dashboard with analytics'. You estimate 3 weeks. They mention they're trying to raise funding next month. What do you do?",
        options: [
            "Build as specified - 3 weeks timeline",
            "Ask what specific metrics investors want to see, build minimal version for demo",
            "Explain 3 weeks is needed for proper dashboard, can't compromise quality",
            "Offer to work extra hours to get it done in 2 weeks"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 2, // just executes
            1: 5, // understands business context, adapts solution
            2: 1, // rigid, misses business need
            3: 3  // helpful but not strategic
        },
        explanation: "Business context changes everything. They don't need a full dashboard - they need specific metrics that look good for investors. Ship what matters in 1 week, not everything in 3."
    },

    // CULTURE - Speed - PMF Obsession
    {
        id: "pmf_1",
        criterion: "pmfObsession",
        pillar: "culture",
        type: "scenario",
        question: "You built a booking feature. After 1 week: 100 bookings created, 0 cancelled. Client is happy. What do you do?",
        options: [
            "Nothing - feature is working well",
            "Add cancellation analytics to dashboard",
            "Talk to users: why no cancellations? Is the flow too hard? Do they just not cancel? Are they stuck?",
            "Build cancellation feature improvements just in case"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 2, // accepts surface metrics
            1: 3, // data-driven but doesn't dig
            2: 5, // user-obsessed, questions assumptions
            3: 1  // builds without understanding
        },
        explanation: "Zero cancellations is weird. Talk to users - maybe the cancel flow is broken and they're calling support instead. Always dig into weird metrics."
    },

    // CULTURE - Simplicity - Feature Judgment
    {
        id: "simplicity_1",
        criterion: "featureJudgment",
        pillar: "culture",
        type: "scenario",
        question: "Client wants to add 'export to Excel, PDF, CSV, and Word' for reports. Users export reports ~2 times per month. What do you do?",
        options: [
            "Build all four formats - client asked for them",
            "Build Excel only - most common use case",
            "Ask client which format they actually use, build that one",
            "Build CSV - simplest, users can convert to anything"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 1, // builds everything asked
            1: 3, // pragmatic guess
            2: 5, // challenges, understands real need
            3: 4  // simplifies aggressively
        },
        explanation: "They probably only use one format. Ask which one they actually use. Build that. Saying no to 3 unnecessary features is better than building 4 mediocre ones."
    },

    {
        id: "simplicity_2",
        criterion: "technicalRestraint",
        pillar: "culture",
        type: "scenario",
        question: "You need to store user preferences (theme, language, timezone). How do you implement it?",
        options: [
            "JSON field in user table",
            "Separate preferences table with flexible key-value structure",
            "JSON field now, move to table if it gets complex later",
            "Use existing settings system if available, otherwise JSON field"
        ],
        correctAnswer: 3,
        scoreMapping: {
            0: 4, // simple and fine
            1: 2, // over-engineers
            2: 3, // pragmatic but premature
            3: 5  // leverages existing, simplest new solution
        },
        explanation: "Use what exists first. If you need something new, start simple (JSON field). Don't build flexible systems for simple needs. Add complexity only when actually needed."
    },

    {
        id: "simplicity_3",
        criterion: "technicalRestraint",
        pillar: "culture",
        type: "scenario",
        question: "You've written similar validation logic in 2 places. Do you create a shared validation function?",
        options: [
            "Yes - extract to shared function immediately",
            "No - wait until you have 3+ use cases",
            "Copy-paste for now, refactor later if needed",
            "Create a validation utility library"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 2, // premature abstraction (2 cases not enough)
            1: 5, // correct - wait for 3+ use cases (KIS principle)
            2: 3, // pragmatic but not ideal
            3: 1  // massive over-engineering
        },
        explanation: "Don't abstract until you have 3+ use cases. Two cases might be coincidental similarity. Wait for the third - then you understand the real pattern. Premature abstraction creates complexity."
    },

    // CULTURE - Simplicity - Communication Clarity
    {
        id: "communication_1",
        criterion: "communicationClarity",
        pillar: "culture",
        type: "scenario",
        question: "Client asks: 'Can we add social login?' You know it's 2 days of OAuth setup + 3 days of testing. How do you respond?",
        options: [
            "'Yes, I'll need to implement OAuth 2.0 flow with PKCE, handle token refresh, etc. About 5 days.'",
            "'Yes, about a week of work for the OAuth integration and testing.'",
            "'Yes, about a week. It'll let users sign in with Google/Facebook instead of creating accounts. Worth it?'",
            "'Sure, no problem. I'll add it.'"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 2, // technical jargon
            1: 3, // clear on timeline
            2: 5, // clear + business context + questions value
            3: 1  // no context given
        },
        explanation: "Clear timeline + what they get + question if it's worth it. No jargon. Help them make informed decision. Communication = translating technical to business value."
    },

    // Additional cross-cutting scenarios
    {
        id: "learning_1",
        criterion: "learningVelocity",
        pillar: "culture",
        type: "scenario",
        question: "Someone reviews your PR: 'This approach won't scale. Consider using a service pattern.' What do you do?",
        options: [
            "Defend your approach - explain why you did it this way",
            "Feel embarrassed, fix it without responding",
            "Thank them, ask questions about service pattern, update PR, apply learning forward",
            "Mark as 'will fix later' and merge"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 1, // defensive
            1: 2, // fixes but doesn't learn
            2: 5, // growth mindset - asks, learns, applies
            3: 1  // ignores feedback
        },
        explanation: "Feedback is learning. Ask questions to understand deeply, fix it, and apply the pattern in future work. Defensiveness blocks learning."
    },

    {
        id: "complexity_handling",
        criterion: "problemDifficulty",
        pillar: "deliverables",
        type: "scenario",
        question: "You need to implement Stripe payments. You've never done it before. Deadline is 5 days. What do you do?",
        options: [
            "Tell your lead you need help or more time - you haven't done Stripe before",
            "Read Stripe docs for 2 days, start implementing on day 3",
            "Find Stripe implementation in similar project, copy and adapt",
            "Implement basic test flow day 1, iterate based on Stripe docs + testing"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 1, // blocked by unknowns
            1: 2, // analysis paralysis
            2: 5, // correct - copy from existing project, adapt to needs
            3: 3  // works but reinventing the wheel
        },
        explanation: "Copy from an existing project. We have similar implementations across projects - find one, copy it, adapt to your needs. Don't reinvent the wheel. This is faster and leverages proven patterns."
    },

    // CULTURE - Proactive Communication (gap identified)
    {
        id: "communication_2",
        criterion: "communicationClarity",
        pillar: "culture",
        type: "scenario",
        question: "Mid-sprint you realize you'll miss your deadline by 2 days. Client hasn't asked for an update yet. What do you do?",
        options: [
            "Wait until daily standup to mention it",
            "Work extra hours to try to meet the deadline anyway",
            "Immediately message your lead: what's delayed, why, and your plan to minimize impact",
            "Wait until client asks for an update"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 3, // communicates but reactive
            1: 2, // hides the problem
            2: 5, // proactive, structured communication
            3: 1  // no ownership of communication
        },
        explanation: "Proactive communication - flag problems BEFORE being asked. Include context (what's delayed, why) and a plan. Never let the client be surprised by a missed deadline."
    },

    // DELIVERABLES - Git Workflow (gap identified)
    {
        id: "git_1",
        criterion: "codeQuality",
        pillar: "deliverables",
        type: "scenario",
        question: "You finished a feature touching 12 files: 3 API routes, 2 models, 4 components, 3 tests. How do you structure your PR?",
        options: [
            "One PR with all 12 files - it's one feature",
            "Split by layer: API PR first, then frontend PR, then tests PR",
            "One PR with organized commits: backend first, then frontend, then tests",
            "Multiple small PRs: one per logical change, merged in dependency order"
        ],
        correctAnswer: 2,
        scoreMapping: {
            0: 3, // works but harder to review
            1: 2, // over-splits, breaks the feature across PRs
            2: 5, // one PR per feature, organized commits for reviewability
            3: 3  // good intent but creates merge complexity
        },
        explanation: "One PR per feature, but with organized commits. Reviewers can follow the logic: backend changes, then frontend, then tests. Don't split a single feature across multiple PRs - that creates merge nightmares."
    },

    // CULTURE - Handling Pressure (gap identified - KPI checklist item)
    {
        id: "pressure_1",
        criterion: "endToEndResponsibility",
        pillar: "culture",
        type: "scenario",
        question: "Client is frustrated on a call about a bug you introduced. They're visibly annoyed. What do you do?",
        options: [
            "Explain the technical reasons why the bug happened",
            "Apologize, take ownership, give timeline for fix, follow up in writing after the call",
            "Stay quiet and let your lead handle it",
            "Promise to fix it immediately and end the call quickly"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 2, // defensive, doesn't help
            1: 5, // professional ownership under pressure
            2: 1, // avoids responsibility
            3: 3  // eager but no structure
        },
        explanation: "Handle pressure professionally. Own it (no excuses), give a concrete timeline, and follow up in writing. The written follow-up shows reliability and gives them something to reference."
    },

    // CULTURE - Asking Smart Questions (gap identified - KPI checklist item)
    {
        id: "questions_1",
        criterion: "communicationClarity",
        pillar: "culture",
        type: "scenario",
        question: "You get a vague ticket: 'Improve the dashboard performance'. No details. What's your first action?",
        options: [
            "Start profiling the dashboard to find slow parts",
            "Ask: 'Which dashboard? What's slow specifically? What's acceptable performance?'",
            "Look at recent commits to dashboard for context",
            "Add caching to the main queries - that usually helps"
        ],
        correctAnswer: 1,
        scoreMapping: {
            0: 3, // proactive but might optimize wrong thing
            1: 5, // clarifies scope before working - smart questions upfront
            2: 2, // investigates but doesn't clarify requirements
            3: 1  // assumes solution without understanding problem
        },
        explanation: "Ask clarifying questions BEFORE starting work. Vague tickets lead to wasted effort. 'Which dashboard? What's slow? What's acceptable?' gets you building the right thing."
    },
];

// Map test answers to evaluation scores (1-5 scale) for evaluation form
// Maps test question criteria to evaluation form criteria
export function calculateEvaluationScoresFromTest(answers) {
    const evaluationScores = {};

    // Map test criteria to evaluation criteria
    const criterionMapping = {
        // DELIVERABLES
        gitWorkflow: ["timeToShip", "shippingVelocity"],
        codeQuality: ["codeQuality", "codeReadability"],
        featureCompleteness: ["productThinking", "featureJudgment", "technicalRestraint"],
        // SKILLS
        architectureUnderstanding: ["problemDifficulty", "complexityManagement", "problemSolving"],
        apiPatterns: ["apiPatterns"],
        frontendPatterns: ["frontendPatterns"],
        databasePatterns: ["problemDifficulty", "complexityManagement"], // Indirect mapping
        integrations: ["problemDifficulty"], // Indirect mapping - complexity handling
        // CULTURE
        communication: ["communicationClarity"],
        ownership: ["endToEndResponsibility", "noBoxes", "commercialMindset", "pmfObsession"],
        learningVelocity: ["learningVelocity"],
    };

    // Calculate scores for each test criterion using scoreMapping
    const testCriterionScores = {};
    TRAINING_TEST_QUESTIONS.forEach((q) => {
        const answer = answers[q.id];
        if (answer !== undefined && q.scoreMapping) {
            const score = q.scoreMapping[answer];
            if (!testCriterionScores[q.criterion]) {
                testCriterionScores[q.criterion] = [];
            }
            testCriterionScores[q.criterion].push(score);
        }
    });

    // Average scores per test criterion
    const avgTestScores = {};
    Object.keys(testCriterionScores).forEach((criterion) => {
        const scores = testCriterionScores[criterion];
        avgTestScores[criterion] = scores.reduce((a, b) => a + b, 0) / scores.length;
    });

    // Map to evaluation criteria by averaging relevant test criteria
    Object.keys(criterionMapping).forEach((evalCriterion) => {
        const testCriteria = criterionMapping[evalCriterion];
        const scores = testCriteria
            .map(tc => avgTestScores[tc])
            .filter(s => s !== undefined);

        if (scores.length > 0) {
            const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
            // Round to nearest integer, but cap at 4 for new trainees
            // They can't be "exceptional" (5) without proving it through actual work
            const cappedScore = Math.min(Math.round(avg), 4);
            evaluationScores[evalCriterion] = cappedScore;
        }
    });

    return evaluationScores;
}

// Map test results to performance scores (1-5 scale)
export function calculatePerformanceScores(testResults) {
    const criterionScores = {};

    // Calculate scores using scoreMapping
    TRAINING_TEST_QUESTIONS.forEach((q) => {
        const answer = testResults[q.id];
        if (answer !== undefined && q.scoreMapping) {
            if (!criterionScores[q.criterion]) {
                criterionScores[q.criterion] = [];
            }
            criterionScores[q.criterion].push(q.scoreMapping[answer]);
        }
    });

    // Average scores per criterion
    const avgScores = {};
    Object.keys(criterionScores).forEach((criterion) => {
        const scores = criterionScores[criterion];
        avgScores[criterion] = scores.reduce((a, b) => a + b, 0) / scores.length;
    });

    // Calculate pillar averages
    const pillars = {
        deliverables: [],
        skills: [],
        culture: []
    };

    TRAINING_TEST_QUESTIONS.forEach((q) => {
        if (avgScores[q.criterion] !== undefined) {
            pillars[q.pillar].push(avgScores[q.criterion]);
        }
    });

    const pillarAverages = {
        deliverables: pillars.deliverables.length > 0
            ? pillars.deliverables.reduce((a, b) => a + b, 0) / pillars.deliverables.length
            : null,
        skills: pillars.skills.length > 0
            ? pillars.skills.reduce((a, b) => a + b, 0) / pillars.skills.length
            : null,
        culture: pillars.culture.length > 0
            ? pillars.culture.reduce((a, b) => a + b, 0) / pillars.culture.length
            : null
    };

    // Calculate weighted overall score
    const overall = (
        (pillarAverages.deliverables || 0) * 0.4 +
        (pillarAverages.skills || 0) * 0.3 +
        (pillarAverages.culture || 0) * 0.3
    );

    // Determine grade
    let grade = "Poor";
    if (overall >= 4.6) grade = "Exceptional";
    else if (overall >= 3.6) grade = "Advanced";
    else if (overall >= 2.6) grade = "Intermediate";
    else if (overall >= 1.6) grade = "Basic";

    return {
        criterionScores: avgScores,
        pillarAverages,
        overall: Math.round(overall * 100) / 100,
        grade,
        nips: Math.round((overall / 5) * 100)
    };
}